import axios from 'axios';
import llmService from './llmService';

/**
 * LLM Playground API - 直接调用 llm.yeap.cloud
 *
 * 不经过 PHP 代理，直接使用用户的 LLM API Token 调用 new-api。
 * 适用于 Chat、Image、Audio、Video、Suno 等 playground 操作。
 * 模型列表、账户管理等仍然走 llmService (PHP 代理)。
 */

const LLM_BASE_URL = 'https://llm.yeap.cloud';

let cachedToken = null;

/**
 * 获取用户的 LLM API Token（带缓存）
 */
async function getToken() {
  if (cachedToken) return cachedToken;
  try {
    const response = await llmService.getTokens({ page: 1, page_size: 50 });
    if (response.success) {
      const tokens = response.data?.items || [];
      const enabled = tokens.filter(t => t.status === 1);
      enabled.sort((a, b) => (a.group === 'default' ? -1 : b.group === 'default' ? 1 : 0));
      if (enabled.length > 0) {
        cachedToken = enabled[0].key;
        return cachedToken;
      }
    }
  } catch {}
  return null;
}

/** 清除 token 缓存 */
function clearTokenCache() {
  cachedToken = null;
}

/**
 * Chat Completions (non-streaming)
 */
async function chatCompletions(data) {
  const token = await getToken();
  if (!token) throw new Error('请先创建 API 密钥');
  const response = await axios.post(`${LLM_BASE_URL}/v1/chat/completions`, {
    ...data,
    stream: false,
  }, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 120000,
  });
  const d = response.data;
  const choice = d.choices?.[0];
  return {
    content: choice?.message?.content || '',
    model: d.model,
    usage: d.usage,
  };
}

/**
 * Chat Completions (streaming) - 返回 Response 对象
 */
async function chatCompletionsStream(data, signal) {
  const token = await getToken();
  if (!token) throw new Error('请先创建 API 密钥');
  return fetch(`${LLM_BASE_URL}/v1/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...data, stream: true }),
    signal,
  });
}

/**
 * Image Generation
 */
async function imageGeneration(data) {
  const token = await getToken();
  if (!token) throw new Error('请先创建 API 密钥');
  const response = await axios.post(`${LLM_BASE_URL}/v1/images/generations`, data, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 120000,
  });
  return response.data; // { data: [{ url, b64_json, revised_prompt }] }
}

/**
 * Audio Speech (TTS) - 返回 Response 对象 (binary)
 */
async function audioSpeech(data, signal) {
  const token = await getToken();
  if (!token) throw new Error('请先创建 API 密钥');
  return fetch(`${LLM_BASE_URL}/v1/audio/speech`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
    signal,
  });
}

/**
 * Video Generation - 提交任务
 */
async function videoGeneration(data) {
  const token = await getToken();
  if (!token) throw new Error('请先创建 API 密钥');
  const response = await axios.post(`${LLM_BASE_URL}/v1/video/generations`, data, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 120000,
  });
  return response.data;
}

/**
 * Video Generation - 查询状态
 */
async function videoStatus(taskId) {
  const token = await getToken();
  if (!token) throw new Error('请先创建 API 密钥');
  const response = await axios.get(`${LLM_BASE_URL}/v1/video/generations/${taskId}`, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000,
  });
  return response.data;
}

/**
 * Video Content - 获取视频内容 (blob)
 */
async function videoContent(taskId) {
  const token = await getToken();
  if (!token) throw new Error('请先创建 API 密钥');
  const response = await axios.get(`${LLM_BASE_URL}/v1/videos/${taskId}/content`, {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
    timeout: 120000,
  });
  return URL.createObjectURL(response.data);
}

/**
 * Suno Music - 提交任务
 * @param {string} action - 'MUSIC' or 'LYRICS'
 * @param {object} data - Suno 参数
 */
async function sunoSubmit(action, data) {
  const token = await getToken();
  if (!token) throw new Error('请先创建 API 密钥');
  const response = await axios.post(`${LLM_BASE_URL}/suno/submit/${action}`, data, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 60000,
  });
  return response.data;
}

/**
 * Suno Music - 查询状态
 */
async function sunoStatus(taskId) {
  const token = await getToken();
  if (!token) throw new Error('请先创建 API 密钥');
  const response = await axios.get(`${LLM_BASE_URL}/suno/fetch/${taskId}`, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30000,
  });
  return response.data;
}

const llmPlaygroundApi = {
  getToken,
  clearTokenCache,
  chatCompletions,
  chatCompletionsStream,
  imageGeneration,
  audioSpeech,
  videoGeneration,
  videoStatus,
  videoContent,
  sunoSubmit,
  sunoStatus,
};

export default llmPlaygroundApi;
